// AuditInvariant — Sprint 19BH Day 1 Step 1.3 · v4 audit framework declarations.
//
// Why this exists (v4-rewrite.md §13.1 + K12):
//   v3 audit-heal was an LLM stage that ran fix-ops as side effects of QA
//   (qa-fact-check.ts). 19BG+2 Day 5 measured its value (sourceRef back-fill,
//   conceptTag fix, optionParity heal) BUT 19BG+3 F35 showed it accumulating
//   cpu_ms inside the same step.do as bundle execution — blowing the 30s
//   Cloudflare Workflows cap when effort=high.
//
//   v4 splits audit into a pure no-LLM TypeScript pass that runs in its own
//   step.do (Day 3 audit.ts) and emits a structured GapReport. The
//   declarative AuditInvariant set drives the audit dispatch (one checker
//   file per `kind`), and the manifest (Day 2 Step 2.3) declares which
//   invariants each stage cares about. No fix is applied here — gaps surface
//   in the regen UI with a `suggestedAction` the user one-clicks.
//
// Why discriminated union (vs string enum + extra fields):
//   Each kind needs different shape (e.g. `min_per_concept` carries `min`
//   number; `tag_in_outline` carries which field; `dedup_by_concept` carries
//   threshold + scope). A discriminated union by `kind` gives strict typing
//   per variant and a single point of failure for "did I forget a kind?"
//   — the dispatch table in audit/registry.ts (Day 3) must cover every kind
//   exhaustively, and TypeScript helps if it doesn't.
//
// Adding a new invariant kind (extensibility checkpoint §12.2):
//   1. Add the literal to AUDIT_INVARIANT_KINDS below
//   2. Add the variant schema to the union below
//   3. Write a checker in packages/ingest/src/audit/rules/<kind>.ts (Day 3)
//   4. Register the kind in some manifest entry's auditInvariants (Day 2)
//   That's it — no framework changes.
import { z } from 'zod';
/**
 * Canonical list of audit invariant kinds. Mirror constant for exhaustive
 * checking + as a quick reference for the rule registry in
 * packages/ingest/src/audit/registry.ts (Day 3).
 */
export const AUDIT_INVARIANT_KINDS = [
    'tag_in_outline', // every item tag must be in CurriculumOutline (I1/I2/I3)
    'concept_coverage', // % of chapter concepts covered by this stage (I4)
    'min_per_concept', // min items per concept per chapter (I5)
    'min_per_chapter', // min items per chapter
    'dedup_by_concept', // text similarity dedup within concept group (I7)
    'format_validity', // option_parity / source_ref / shared_concepts lint
    'omission_rate', // % of concepts omitted via ConceptOmission (I6)
    'custom', // escape hatch · checkerId references audit/rules/custom/*
];
/** Identifies which tag field on an item is being audited. */
export const ItemTagFieldSchema = z.enum(['caseIds', 'topicIds', 'conceptIds']);
/** Scope at which an invariant evaluates (chapter-level vs course-wide). */
export const InvariantScopeSchema = z.enum(['chapter', 'course']);
/** Format-validity rule ids · lifted from 19BG+2 lint rules into Day 3 audit. */
export const FormatValidityRuleIdSchema = z.enum([
    'option_parity', // MCQ options balanced for length/structure (existing lint)
    'source_ref', // ChunkRef snippet substring-matches source (existing lint)
    'shared_concepts', // sharedConcepts of a chapter bundle ⊆ outline (existing lint)
]);
// ─── 8 variant schemas ──────────────────────────────────────────────────────
const TagInOutlineInvariantSchema = z.object({
    kind: z.literal('tag_in_outline'),
    field: ItemTagFieldSchema
        .describe('Which tag field on the item to audit against the outline.'),
}).passthrough();
const ConceptCoverageInvariantSchema = z.object({
    kind: z.literal('concept_coverage'),
    scope: z.literal('chapter'),
    min: z.number().min(0).max(1)
        .describe('Minimum fraction of chapter concepts that must be covered (0–1).'),
}).passthrough();
const MinPerConceptInvariantSchema = z.object({
    kind: z.literal('min_per_concept'),
    scope: z.literal('chapter'),
    min: z.number().int().min(0)
        .describe('Minimum item count per concept per chapter.'),
}).passthrough();
const MinPerChapterInvariantSchema = z.object({
    kind: z.literal('min_per_chapter'),
    quantity: z.number().int().min(0)
        .describe('Minimum item count per chapter.'),
}).passthrough();
const DedupByConceptInvariantSchema = z.object({
    kind: z.literal('dedup_by_concept'),
    scope: InvariantScopeSchema,
    textSimilarityThreshold: z.number().min(0).max(1)
        .describe('Jaccard / cosine threshold for considering two items duplicates. Default 0.85 in Day 3 manifest.'),
}).passthrough();
const FormatValidityInvariantSchema = z.object({
    kind: z.literal('format_validity'),
    ruleId: FormatValidityRuleIdSchema
        .describe('Which lint rule to run (lifted from 19BG+2 lint into audit Day 3).'),
}).passthrough();
const OmissionRateInvariantSchema = z.object({
    kind: z.literal('omission_rate'),
    max: z.number().min(0).max(1)
        .describe('Maximum allowed omission rate (fraction of concepts soft-escaped) before audit warns. I6 = 0.2 default.'),
}).passthrough();
const CustomInvariantSchema = z.object({
    kind: z.literal('custom'),
    checkerId: z.string().min(1).max(64)
        .describe('Reference to a custom checker file under audit/rules/custom/<checkerId>.ts.'),
}).passthrough();
/**
 * Discriminated union of all audit invariant variants. Dispatch in
 * audit/registry.ts (Day 3) uses the `kind` discriminator exhaustively.
 */
export const AuditInvariantSchema = z.discriminatedUnion('kind', [
    TagInOutlineInvariantSchema,
    ConceptCoverageInvariantSchema,
    MinPerConceptInvariantSchema,
    MinPerChapterInvariantSchema,
    DedupByConceptInvariantSchema,
    FormatValidityInvariantSchema,
    OmissionRateInvariantSchema,
    CustomInvariantSchema,
]);
//# sourceMappingURL=audit-invariant.js.map